/**
 * BrandImageField — reusable component for logo / favicon / hero image upload + custom URL.
 *
 * Spec 0015: Two modes —
 *   (1) Upload mode (default): live preview + Upload/Replace + Clear; "use custom URL" link.
 *   (2) Custom URL mode: full URL field split at last "/" into *Image + *BaseUrl; live preview + Clear; "switch back" link.
 *
 * Props: asset type, size hint, editable *Image/*BaseUrl state, composed *Url for preview,
 * globalAssetBaseUrl, upload handler, isNew flag (upload disabled in create mode).
 */
import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useCopy } from '@/hooks/useCopy';
import type { ApiError } from '@/api/errors';

export interface BrandImageFieldProps {
  /** Asset type (for labels/hints) */
  assetType: 'logo' | 'favicon' | 'hero';
  /** Editable image key/filename (or full URL) */
  image: string;
  /** Editable override base URL (null = inherit global) */
  baseUrl: string;
  /** Read-only composed effective URL (for preview) */
  composedUrl: string;
  /** Global default base URL */
  globalAssetBaseUrl: string;
  /** onChange handler for image/baseUrl */
  onChange: (image: string, baseUrl: string) => void;
  /** Upload handler (returns updated brand with new URLs) */
  onUpload?: (file: File) => Promise<void>;
  /** Whether in create mode (upload disabled) */
  isNew: boolean;
  /** Pending upload state */
  uploadPending?: boolean;
  /** Upload error */
  uploadError?: ApiError | null;
  /** Spec 0021 — Optional size hint override (template-aware hero hints) */
  sizeHint?: string;
}

/**
 * Split a full URL at the last "/" into base + filename.
 * Example: "https://cdn.acme.com/brands/logo.png" → { base: "https://cdn.acme.com/brands", filename: "logo.png" }
 * Keeps any query/fragment with the filename.
 */
function splitUrlAtLastSlash(url: string): { base: string; filename: string } | null {
  const trimmed = url.trim();
  if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
    return null;
  }

  // Find last slash after protocol
  const protocolEnd = trimmed.indexOf('://') + 3;
  const pathStart = trimmed.indexOf('/', protocolEnd);
  if (pathStart === -1) {
    // No path after host
    return null;
  }

  const lastSlash = trimmed.lastIndexOf('/');
  if (lastSlash === pathStart || lastSlash === trimmed.length - 1) {
    // Only host or trailing slash with no filename
    return null;
  }

  const base = trimmed.substring(0, lastSlash);
  const filename = trimmed.substring(lastSlash + 1);

  if (!filename) {
    return null;
  }

  return { base, filename };
}

/**
 * Compose a URL from base + filename (inverse of split).
 */
function composeUrl(base: string, filename: string): string {
  if (!base || !filename) return '';
  // Strip trailing slash from base to avoid doubling
  const cleanBase = base.endsWith('/') ? base.slice(0, -1) : base;
  return `${cleanBase}/${filename}`;
}

export function BrandImageField(props: BrandImageFieldProps) {
  const { t } = useCopy();
  const {
    assetType,
    image,
    baseUrl,
    composedUrl,
    onChange,
    onUpload,
    isNew,
    uploadPending,
    uploadError,
    sizeHint: sizeHintOverride,
  } = props;

  const [mode, setMode] = useState<'upload' | 'custom-url'>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [customUrlInput, setCustomUrlInput] = useState('');
  const [customUrlError, setCustomUrlError] = useState<string | null>(null);

  const hasImage = Boolean(composedUrl);

  // Switch to custom URL mode
  function onUseCustomUrl() {
    // Prepopulate with current composed URL if available
    setCustomUrlInput(composedUrl || '');
    setMode('custom-url');
  }

  // Switch back to upload mode
  function onSwitchBackToUpload() {
    setMode('upload');
    setCustomUrlInput('');
    setCustomUrlError(null);
    setSelectedFile(null);
  }

  // Clear image (empty both fields)
  function onClear() {
    onChange('', '');
    setSelectedFile(null);
    setCustomUrlInput('');
    setCustomUrlError(null);
  }

  // Handle custom URL input change
  function onCustomUrlChange(value: string) {
    setCustomUrlInput(value);
    setCustomUrlError(null);

    if (!value.trim()) {
      // Empty = clear
      onChange('', '');
      return;
    }

    const split = splitUrlAtLastSlash(value);
    if (!split) {
      setCustomUrlError(
        t(
          'brands.fields.customUrlInvalid',
          'Must be a valid http(s) URL with a filename (e.g. https://cdn.example.com/logo.png)',
        ),
      );
      return;
    }

    // Valid split → update state
    onChange(split.filename, split.base);
  }

  // Handle file selection
  function onFileChange(e: { target: { files?: FileList | null } }) {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
  }

  // Handle upload button click
  async function handleUpload() {
    if (!selectedFile || !onUpload) return;
    await onUpload(selectedFile);
    setSelectedFile(null);
  }

  // Derive labels
  const typeLabel = t(`brands.fields.${assetType}Image`, assetType);
  const sizeHint = sizeHintOverride || t(`brands.fields.${assetType}SizeHint`, '');

  return (
    <Stack spacing={2}>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          fontWeight: 500,
        }}
      >
        {typeLabel}
      </Typography>
      {mode === 'upload' ? (
        <>
          {/* Upload mode */}
          {hasImage && (
            <Box>
              <Box
                component="img"
                src={composedUrl}
                alt={`${typeLabel} preview`}
                sx={{
                  maxWidth: '100%',
                  maxHeight: assetType === 'favicon' ? 128 : 200,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  display: 'block',
                }}
              />
            </Box>
          )}

          {!hasImage && (
            <Alert severity="info" sx={{ py: 3, textAlign: 'center' }}>
              <Typography variant="body2">
                {t('brands.fields.noImageYet', 'No image uploaded yet')}
              </Typography>
            </Alert>
          )}

          {isNew ? (
            <Alert severity="info">
              {t('brands.fields.uploadCreateHint', 'Save the brand first, then upload an image.')}
            </Alert>
          ) : (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
              }}
            >
              <Button variant="outlined" component="label" disabled={uploadPending ? true : false}>
                {hasImage
                  ? t('brands.fields.replaceImage', 'Replace image')
                  : t('brands.fields.chooseFile', 'Choose file')}
                <input type="file" hidden accept="image/png,image/jpeg" onChange={onFileChange} />
              </Button>
              {selectedFile && (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {selectedFile.name}
                </Typography>
              )}
              <Button
                variant="contained"
                disabled={!selectedFile || uploadPending ? true : false}
                onClick={() => void handleUpload()}
                startIcon={uploadPending ? <CircularProgress size={16} /> : null}
              >
                {uploadPending
                  ? t('brands.fields.uploading', 'Uploading…')
                  : t('brands.fields.upload', 'Upload')}
              </Button>
              {hasImage && (
                <IconButton
                  aria-label={t('brands.fields.clearImage', 'Clear image')}
                  onClick={onClear}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Stack>
          )}

          {uploadError && (
            <Alert severity="error">
              {t(`errors.codes.${uploadError.code}`, uploadError.message)}
            </Alert>
          )}

          <Link
            component="button"
            variant="body2"
            onClick={onUseCustomUrl}
            sx={{ alignSelf: 'flex-start' }}
          >
            {t('brands.fields.useCustomUrl', 'Use a custom image URL')}
          </Link>
        </>
      ) : (
        <>
          {/* Custom URL mode */}
          <TextField
            fullWidth
            label={t('brands.fields.customImageUrl', 'Image URL')}
            value={customUrlInput}
            onChange={(e) => onCustomUrlChange(e.target.value)}
            placeholder="https://cdn.example.com/logo.png"
            error={Boolean(customUrlError)}
            helperText={customUrlError || undefined}
          />

          {image && baseUrl && !customUrlError && customUrlInput && (
            <Box>
              <Typography
                variant="caption"
                gutterBottom
                sx={{
                  color: 'text.secondary',
                  display: 'block',
                }}
              >
                {t('brands.fields.parsedAs', 'Parsed as:')} <strong>{image}</strong>{' '}
                {t('brands.fields.servedFrom', 'served from')} <strong>{baseUrl}</strong>
              </Typography>
              <Box
                component="img"
                src={composeUrl(baseUrl, image)}
                alt={`${typeLabel} preview`}
                sx={{
                  maxWidth: '100%',
                  maxHeight: assetType === 'favicon' ? 128 : 200,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  display: 'block',
                  mt: 1,
                }}
              />
            </Box>
          )}

          <Stack direction="row" spacing={1}>
            {(image || customUrlInput) && (
              <Button variant="outlined" onClick={onClear} startIcon={<DeleteIcon />}>
                {t('brands.fields.clearImage', 'Clear image')}
              </Button>
            )}
            <Link
              component="button"
              variant="body2"
              onClick={onSwitchBackToUpload}
              sx={{ alignSelf: 'center' }}
            >
              {t('brands.fields.switchBackToUpload', 'Switch back to upload')}
            </Link>
          </Stack>
        </>
      )}
      {sizeHint && mode === 'upload' && (
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
          }}
        >
          {sizeHint}
        </Typography>
      )}
    </Stack>
  );
}
